import { useRef, useState } from "react";
import { C, MONO, UI } from "../results/ui-tokens";

/**
 * Reusable "import a competency matrix from a file" panel. Format-agnostic about
 * model vs. discipline — the parent owns parse + map + state population and
 * returns an error string (or `null` on success) from `onApply`. This component
 * only does the file IO + paste box + result feedback.
 *
 * One file path: a picked `.json`/`.yaml`/`.yml` file is read to text and a
 * pasted blob both flow through the same `onApply(text)` (the parser handles
 * JSON and YAML uniformly).
 */
export interface MatrixFileImportProps {
  /** Parse + apply the file text into the draft. Return `null` on success, or a
   *  human-readable error message to display. */
  onApply: (text: string) => string | null;
  /** Confirmation copy shown after a successful import (what got populated). */
  successMessage: string;
  disabled?: boolean;
  testidPrefix: string;
  /** One-line description of the expected file, shown in the panel. */
  hint: string;
}

export function MatrixFileImport({
  onApply,
  successMessage,
  disabled,
  testidPrefix,
  hint,
}: MatrixFileImportProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  function apply(content: string) {
    const err = onApply(content);
    setError(err);
    setApplied(err === null);
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const content = await file.text();
    setText(content);
    apply(content);
    // Allow re-picking the same file after a fix.
    e.target.value = "";
  }

  return (
    <div
      data-testid={`${testidPrefix}-file-import`}
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        background: C.white,
      }}
    >
      <div style={{ ...UI, fontSize: 13, fontWeight: 600, color: C.ink }}>
        Importar de arquivo (JSON / YAML)
      </div>
      <p style={{ ...UI, fontSize: 12, color: C.ink50, lineHeight: 1.5, margin: "4px 0 12px" }}>
        {hint} Preenche o editor abaixo — revise e ajuste antes de salvar.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept=".json,.yaml,.yml,application/json,text/yaml"
        onChange={handleFile}
        disabled={disabled}
        data-testid={`${testidPrefix}-file-input`}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          data-testid={`${testidPrefix}-file-pick`}
          style={{
            ...UI,
            border: `1px solid ${C.ink20}`,
            background: C.white,
            borderRadius: 999,
            padding: "7px 14px",
            fontSize: 12.5,
            fontWeight: 600,
            color: C.ink70,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          Escolher arquivo…
        </button>
        <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>ou cole abaixo</span>
      </div>

      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setApplied(false);
        }}
        placeholder={"name: ...\nlevels: { min: 0, max: 7 }\ncrossAxes:\n  - label: ..."}
        rows={5}
        disabled={disabled}
        data-testid={`${testidPrefix}-file-paste`}
        spellCheck={false}
        style={{
          ...MONO,
          width: "100%",
          marginTop: 10,
          padding: 12,
          fontSize: 12,
          lineHeight: 1.5,
          border: `1px solid ${C.ink10}`,
          borderRadius: 8,
          background: C.paper,
          color: C.ink,
          resize: "vertical",
        }}
      />

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <button
          type="button"
          onClick={() => apply(text)}
          disabled={disabled || text.trim().length === 0}
          data-testid={`${testidPrefix}-file-apply`}
          style={{
            ...UI,
            border: 0,
            background: text.trim().length === 0 ? C.red20 : C.red,
            color: C.white,
            borderRadius: 999,
            padding: "7px 16px",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: disabled || text.trim().length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Aplicar ao editor
        </button>
        {applied && !error && (
          <span style={{ ...UI, fontSize: 12.5, fontWeight: 500, color: C.green }}>
            ✓ {successMessage}
          </span>
        )}
      </div>

      {error && (
        <pre
          data-testid={`${testidPrefix}-file-error`}
          style={{
            ...MONO,
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 8,
            background: C.amberBg,
            border: `1px solid ${C.amberFg}`,
            color: C.amberFg,
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {error}
        </pre>
      )}
    </div>
  );
}
